/**
 * LLM Scan Results Page
 *
 * Streamlined security report with:
 * - Risk Scorecard (pass rate, severity, compliance)
 * - Findings with inline remediation (expandable)
 * - All Tests table (for audit)
 */

import { useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "@/hooks/useApi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { AlertCircle, Table as TableIcon, Share2, Users } from "lucide-react";
import { RiskScorecard } from "@/components/llm/RiskScorecard";
import { FindingsWithRemediation } from "@/components/llm/FindingsWithRemediation";
import { VulnerabilitiesTable } from "@/components/llm/VulnerabilitiesTable";
import { VulnerabilityDetailModal } from "@/components/llm/VulnerabilityDetailModal";
import { ShareScanModal } from "@/components/llm/ShareScanModal";
import { useLLMScanSummary, useLLMScanResults } from "@/hooks/useLLMScans";
import { useOrganizationContext } from "@/providers/OrganizationProvider";
import { useAnalytics } from "@/hooks/useAnalytics";
import { updateScanVisibility } from "@/services/llmScanService";
import { classifyIssues, calculateVerdict } from "@/utils/issueClassification";
import type { LLMScanResult, LLMScanSummary } from "@/types/llm";

export function LLMScanResultsPage() {
  useApi();
  const { user } = useOrganizationContext();
  const { track } = useAnalytics();
  const queryClient = useQueryClient();

  const { scanUuid } = useParams<{ scanUuid: string }>();
  const [selectedResult, setSelectedResult] = useState<LLMScanResult | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [tablePage, setTablePage] = useState(1);
  const tableItemsPerPage = 10;

  const isScanInProgress = (status?: string) => status === "pending" || status === "running";

  const {
    data: summary,
    isLoading: summaryLoading,
    error: summaryError,
  } = useLLMScanSummary(scanUuid || "", { autoRefresh: true });

  const visibilityMutation = useMutation({
    mutationFn: (isOrgVisible: boolean) => updateScanVisibility(scanUuid || "", isOrgVisible),
    onMutate: async (isOrgVisible) => {
      await queryClient.cancelQueries({ queryKey: ["llm-scan-summary", scanUuid] });
      const prev = queryClient.getQueryData<LLMScanSummary>(["llm-scan-summary", scanUuid]);
      if (prev) {
        queryClient.setQueryData(["llm-scan-summary", scanUuid], {
          ...prev,
          scan: { ...prev.scan, is_org_visible: isOrgVisible },
        });
      }
      return { prev };
    },
    onSuccess: (updatedScan) => {
      queryClient.setQueryData<LLMScanSummary>(["llm-scan-summary", scanUuid], (old) =>
        old ? { ...old, scan: updatedScan } : old
      );
      queryClient.invalidateQueries({ queryKey: ["llm-scans"] });
    },
    onError: (_err, _vars, context) => {
      if (context?.prev) {
        queryClient.setQueryData(["llm-scan-summary", scanUuid], context.prev);
      }
    },
  });

  const scanInProgress = isScanInProgress(summary?.scan.status);
  const autoRefreshInterval = scanInProgress ? 5000 : false;

  const {
    data: resultsData,
    isLoading: resultsLoading,
    error: resultsError,
  } = useLLMScanResults(
    scanUuid || "",
    { page: 1, items_per_page: 1000 },
    {
      refetchInterval: autoRefreshInterval,
    }
  ); // Fetch all for table pagination

  // Get results early (empty array if not loaded yet)
  const results = resultsData?.data || [];

  // Classify issues and calculate verdict - MUST be called before any early returns
  const classifiedIssues = useMemo(() => classifyIssues(results), [results]);
  const scanFailed = summary?.scan.status === "failed" || summary?.scan.status === "cancelled";
  // Don't compute verdict while results are still loading for a completed scan —
  // otherwise calculateVerdict([]) returns PASS before real results arrive.
  const verdictLoading = !scanInProgress && !scanFailed && resultsLoading;
  const verdict = useMemo(() => {
    if (scanFailed) {
      return {
        verdict: "FAIL" as const,
        blockingCount: 0,
        advisoryCount: 0,
        summary: "Scan execution failed",
      };
    }
    if (verdictLoading) {
      return undefined;
    }
    return calculateVerdict(results);
  }, [results, scanFailed, verdictLoading]);
  const blockingIssues = useMemo(
    () => classifiedIssues.filter((r) => r.category === "blocking" && r.status === "FAIL"),
    [classifiedIssues]
  );
  const advisoryIssues = useMemo(
    () => classifiedIssues.filter((r) => r.category === "advisory" && r.status === "FAIL"),
    [classifiedIssues]
  );

  if (!scanUuid) {
    return (
      <div className="flex flex-1 bg-muted/10 px-4 py-6 lg:px-6">
        <Card className="w-full rounded-[15px] shadow-none">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              <div className="text-sm sm:text-base">Invalid scan UUID</div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const handleResultClick = (result: LLMScanResult) => {
    setSelectedResult(result);
    setIsModalOpen(true);
  };

  if (summaryError || resultsError) {
    return (
      <div className="flex flex-1 bg-muted/10 px-4 py-6 lg:px-6">
        <Card className="w-full rounded-[15px] shadow-none">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5 flex-shrink-0" />
              <div>
                <div className="font-medium text-sm sm:text-base">Error loading scan results</div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {summaryError?.message || resultsError?.message || "Unknown error"}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (summaryLoading || !summary) {
    return (
      <div className="flex flex-1 flex-col space-y-6 bg-muted/10 px-4 py-6 lg:px-6">
        <Skeleton className="h-48 w-full rounded-[15px]" />
        <Skeleton className="h-32 w-full rounded-[15px]" />
        <Skeleton className="h-64 w-full rounded-[15px]" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col space-y-6 bg-muted/10 px-4 py-6 lg:px-6 [&_.rounded-md]:rounded-[15px] [&_.rounded-lg]:rounded-[15px]">
      {/* Header with Share Button and Org Visibility Toggle */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-xl font-medium">AI security review</h1>
          <p className="text-sm text-muted-foreground">
            Review scan findings, risk, and remediation guidance
          </p>
        </div>
        <div className="flex items-center gap-4">
          {/* Organization Visibility Toggle - owner only */}
          {summary.scan.user_id != null && summary.scan.user_id === user?.id && (
            <div className="flex items-center gap-2 rounded-full border bg-white px-3 py-2">
              <Users className="h-4 w-4 text-muted-foreground" />
              <Label htmlFor="org-visible" className="text-sm cursor-pointer">
                Visible to organization
              </Label>
              <Switch
                id="org-visible"
                checked={summary.scan.is_org_visible}
                onCheckedChange={(checked) => visibilityMutation.mutate(checked)}
                disabled={visibilityMutation.isPending}
              />
            </div>
          )}
          <Button
            onClick={() => {
              if (scanUuid) {
                track({
                  name: "scan_results_shared",
                  properties: { scan_uuid: scanUuid },
                });
              }
              setIsShareModalOpen(true);
            }}
            variant="outline"
            className="rounded-full bg-white"
          >
            <Share2 className="h-4 w-4 mr-2" />
            Share Report
          </Button>
        </div>
      </div>

      <RiskScorecard
        summary={summary}
        results={results}
        isLoading={summaryLoading || verdictLoading}
        verdict={verdict}
      />

      <FindingsWithRemediation
        results={results}
        isLoading={resultsLoading}
        isPartialData={scanInProgress}
        scanFailed={scanFailed}
        blockingIssues={blockingIssues}
        advisoryIssues={advisoryIssues}
      />

      {/* All Tests Table - For audit/verification */}
      <Card className="rounded-[15px] shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <TableIcon className="h-4 w-4" />
            Audit trail ({scanInProgress ? `${summary.scan.progress}%` : summary.scan.total_tests})
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {scanFailed
              ? "No test results available — the scan failed before tests could run."
              : scanInProgress
                ? "Results are still arriving as the scan progresses."
                : "Complete test results for audit and verification"}
          </p>
        </CardHeader>
        <CardContent>
          <VulnerabilitiesTable
            results={results}
            isLoading={resultsLoading}
            scanStatus={summary.scan.status}
            onResultClick={handleResultClick}
            page={tablePage}
            itemsPerPage={tableItemsPerPage}
            onPageChange={setTablePage}
          />
        </CardContent>
      </Card>

      {/* Detail Modal - Still available for full details from table */}
      <VulnerabilityDetailModal
        result={selectedResult}
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
      />

      {/* Share Modal */}
      {scanUuid && (
        <ShareScanModal
          scanUuid={scanUuid}
          open={isShareModalOpen}
          onOpenChange={setIsShareModalOpen}
        />
      )}
    </div>
  );
}
